import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import FilterPanel, { type FilterState } from "@/pages/category/components/FilterPanel";
import { movies } from "@/mocks/movies";
import { filterMovies, paginate } from "@/utils/filter";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 12;

const categoryMeta: Record<string, { title: string; desc: string; keywords: string }> = {
  movie: {
    title: "电影频道",
    desc: "97视频电影频道精选院线大片与经典佳片，涵盖科幻、动作、悬疑、剧情等多种类型，支持在线观看，畅享高清观影体验。",
    keywords: "97视频电影,在线看电影,高清电影,院线大片,科幻电影,悬疑电影",
  },
  tv: {
    title: "电视剧频道",
    desc: "97视频电视剧频道汇聚热播国剧与海外剧集，悬疑、古装、都市、犯罪题材应有尽有，支持在线观看，追剧不停。",
    keywords: "97视频电视剧,在线追剧,热播剧集,国产剧,悬疑剧,古装剧",
  },
  variety: {
    title: "综艺频道",
    desc: "97视频综艺频道汇聚真人秀、音乐竞演、生活慢综艺等当下热门综艺节目，承包你的欢乐时光。",
    keywords: "97视频综艺,在线看综艺,真人秀,音乐竞演,热门综艺",
  },
  all: {
    title: "全部影视",
    desc: "97视频全部影视聚合电影、电视剧、综艺资源，支持地区、年份、类型多维筛选，总有一部适合你。",
    keywords: "97视频,影视大全,在线观看影视,电影,电视剧,综艺",
  },
};

const defaultFilters: FilterState = {
  regions: [],
  years: [],
  genres: [],
  sort: "hot",
};

export default function Category() {
  const { type = "all" } = useParams();
  const [searchParams] = useSearchParams();
  const meta = categoryMeta[type] || categoryMeta.all;

  const [filters, setFilters] = useState<FilterState>({
    ...defaultFilters,
    sort: searchParams.get("sort") || "hot",
  });
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  usePageMeta({
    title: `${meta.title} - 97视频`,
    description: meta.desc,
    keywords: meta.keywords,
  });

  const filtered = useMemo(
    () => filterMovies(movies, { type, ...filters }),
    [type, filters],
  );

  const { items, totalPages, total } = useMemo(
    () => paginate(filtered, page, PAGE_SIZE),
    [filtered, page],
  );

  const handleFilterChange = (next: FilterState) => {
    setFilters(next);
    setPage(1);
  };

  const handleReset = () => {
    setFilters({ ...defaultFilters });
    setPage(1);
  };

  const goToPage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="mx-auto max-w-[1400px] px-4 py-6 md:px-8 md:py-8">
        <nav className="mb-4 flex items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="cursor-pointer hover:text-accent-500">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-800">{meta.title}</span>
        </nav>

        <div className="mb-6 rounded-lg border border-background-200/70 bg-gradient-to-r from-primary-900/60 to-background-100 p-5 md:p-6">
          <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            {meta.title}
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
            {meta.desc}
          </p>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row">
          <aside className="w-full lg:w-[280px] lg:shrink-0">
            <button
              type="button"
              onClick={() => setMobileFilterOpen((v) => !v)}
              className="mb-3 flex w-full cursor-pointer items-center justify-between rounded-lg border border-background-200/70 bg-background-100 px-4 py-3 text-sm font-medium text-foreground-800 lg:hidden"
            >
              <span className="flex items-center gap-2">
                <i className="ri-filter-3-line text-accent-500" aria-hidden="true"></i>
                筛选条件
              </span>
              <i
                className={mobileFilterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}
                aria-hidden="true"
              ></i>
            </button>
            <div
              className={`${mobileFilterOpen ? "block" : "hidden"} lg:sticky lg:top-20 lg:block`}
            >
              <FilterPanel
                state={filters}
                onChange={handleFilterChange}
                onReset={handleReset}
              />
            </div>
          </aside>

          <section className="flex-1">
            <div className="mb-4 flex items-center justify-between text-sm text-foreground-500">
              <span>
                共找到 <strong className="text-accent-500">{total}</strong> 部影视
              </span>
            </div>

            {items.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 xl:grid-cols-4">
                  {items.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
                <Pagination
                  current={page}
                  totalPages={totalPages}
                  onChange={goToPage}
                />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100 py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-movie-2-line text-3xl" aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                  没有找到符合条件的影视
                </h3>
                <p className="mt-2 text-sm text-foreground-500">
                  试试调整或重置筛选条件，发现更多精彩内容
                </p>
                <button
                  type="button"
                  onClick={handleReset}
                  className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                >
                  重置筛选
                </button>
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}